import { emailCampaignsApi } from '@/store/emailCampaignService';
import {
  STEP_SETUP,
  STEP_RECIPIENTS_TYPE,
  STEP_RECIPIENTS_AUTO,
  STEP_RECIPIENTS_MANUAL,
  STEP_TEMPLATE_LIST,
  STEP_TEMPLATE_EDITOR,
  STEP_REVIEW,
} from '@/pages/emailCampaigns/createCampaign/constants/steps';
import { RECIPIENTS_TYPE_REGULAR, RECIPIENTS_TYPE_AUTOMATED, RECIPIENTS_TYPE_MODERATED } from '@/pages/emailCampaigns/constants/recipients';
import { campaignDoesNotBelongToCompany } from '@/pages/emailCampaigns/util/helpers';

const initializeForm = () => ({
  step: STEP_SETUP,
  isSaving: false,
  isLoading: false,
  data: null,
  templateId: null,
  recipientsType: null,
  recipientGroups: [],
});

const initializeSingleTemplate = () => ({
  isLoading: false,
  data: null,
});

const initializeTemplates = () => ({
  isLoading: false,
  data: [],
});

const initializeCompanySettings = () => ({
  isLoading: false,
  data: null,
});

const InitialState = {
  form: initializeForm(),
  singleTemplate: initializeSingleTemplate(),
  templates: initializeTemplates(),
  companySettings: initializeCompanySettings(),
};

const mutations = {
  // form mutations
  resetForm: (state) => Object.assign(state, {
    form: initializeForm(),
    singleTemplate: initializeSingleTemplate(),
    templates: initializeTemplates(),
  }),
  nextStep: (state) => {
    let nextStep;
    switch (state.form.step) {
      case STEP_SETUP:
        nextStep = STEP_RECIPIENTS_TYPE;
        break;
      case STEP_RECIPIENTS_TYPE:
        // determine which recipients page to goto next
        if (state.form.data
          && (state.form.data.type === RECIPIENTS_TYPE_AUTOMATED
            || state.form.data.type === RECIPIENTS_TYPE_MODERATED)
        ) {
          nextStep = STEP_RECIPIENTS_AUTO;
        } else {
          nextStep = STEP_RECIPIENTS_MANUAL;
        }
        break;
      case STEP_RECIPIENTS_AUTO:
      case STEP_RECIPIENTS_MANUAL:
        nextStep = STEP_TEMPLATE_LIST;
        break;
      case STEP_TEMPLATE_LIST:
        nextStep = STEP_TEMPLATE_EDITOR;
        break;
      case STEP_TEMPLATE_EDITOR:
        nextStep = STEP_REVIEW;
        break;
      default:
        nextStep = STEP_SETUP;
    }

    return Object.assign(state, {
      form: Object.assign(state.form, {
        step: nextStep,
      }),
    });
  },
  backStep: (state) => {
    let nextStep;
    switch (state.form.step) {
      case STEP_SETUP:
        nextStep = STEP_SETUP;
        break;
      case STEP_RECIPIENTS_TYPE:
        nextStep = STEP_SETUP;
        break;
      case STEP_RECIPIENTS_AUTO:
      case STEP_RECIPIENTS_MANUAL:
        nextStep = STEP_RECIPIENTS_TYPE;
        break;
      case STEP_TEMPLATE_LIST:
        if (state.form.data
          && (state.form.data.type === RECIPIENTS_TYPE_AUTOMATED
            || state.form.data.type === RECIPIENTS_TYPE_MODERATED)
        ) {
          nextStep = STEP_RECIPIENTS_AUTO;
        } else {
          nextStep = STEP_RECIPIENTS_MANUAL;
        }
        break;
      case STEP_TEMPLATE_EDITOR:
        nextStep = STEP_TEMPLATE_LIST;
        break;
      case STEP_REVIEW:
        nextStep = STEP_TEMPLATE_EDITOR;
        break;
      default:
        nextStep = STEP_SETUP;
    }

    return Object.assign(state, {
      form: Object.assign(state.form, {
        step: nextStep,
      }),
    });
  },
  gotoStep: (state, step) => Object.assign(state, {
    form: Object.assign(state.form, {
      step,
    }),
  }),
  setLoading: (state, isLoading) => Object.assign(state, {
    form: Object.assign(state.form, {
      isLoading,
    }),
  }),
  setSaving: (state, isSaving) => Object.assign(state, {
    form: Object.assign(state.form, {
      isSaving,
    }),
  }),
  setCampaignData: (state, data) => Object.assign(state, {
    form: Object.assign(state.form, {
      data,
    }),
  }),
  selectTemplate: (state, templateId) => Object.assign(state, {
    form: Object.assign(state.form, {
      templateId,
    }),
  }),
  selectRecipientsType: (state, newType) => {
    let recipientsType = null;

    if (newType === RECIPIENTS_TYPE_REGULAR) {
      recipientsType = RECIPIENTS_TYPE_REGULAR;
    } else if (
      newType === RECIPIENTS_TYPE_AUTOMATED
      || newType === RECIPIENTS_TYPE_MODERATED
    ) {
      // normalize moderated to automated value because moderated is a substep of
      // automated and cannot be chosen on the recipients type step
      recipientsType = RECIPIENTS_TYPE_AUTOMATED;
    }

    return Object.assign(state, {
      form: Object.assign(state.form, {
        recipientsType,
      }),
    });
  },
  setRecipientGroups: (state, recipientGroups) => Object.assign(state, {
    form: Object.assign(state.form, {
      recipientGroups,
    }),
  }),
  appendRecipient: (state, item) => {
    const foundGroup = state.form.recipientGroups.find((group) => group.uid === item.uid);

    // add if item does not exist in the list, else keep same list
    const newRecipientsGroup = !foundGroup
      ? [
        ...state.form.recipientGroups,
        item,
      ]
      : state.form.recipientGroups;

    return Object.assign(state, {
      form: Object.assign(state.form, {
        recipientGroups: newRecipientsGroup,
      }),
    });
  },
  removeRecipient: (state, id) => {
    // keep recipients that do not match provided id
    const newRecipientList = state.form.recipientGroups.filter((item) => item.uid !== id);

    return Object.assign(state, {
      form: Object.assign(state.form, {
        recipientGroups: newRecipientList,
      }),
    });
  },
  removeRecipientsByOwnerAndSource: (state, { owner, source, excludedIds = [] }) => {
    const newRecipientList = state.form.recipientGroups.filter(
      (item) => {
        // keep recipient if uid is excluded
        if (excludedIds.includes(item.uid)) {
          return true;
        }

        // keep recipient if it does not have matching owner & source
        if (!(item.owner === owner && item.source === source)) {
          return true;
        }

        return false;
      },
    );

    return Object.assign(state, {
      form: Object.assign(state.form, {
        recipientGroups: newRecipientList,
      }),
    });
  },
  removeShareholderRangeRecipients: (state) => {
    const newRecipientList = state.form.recipientGroups
      .filter((item) => {
        // keep recipients that are not shareholders
        if (item.source !== 'shareholders') {
          return true;
        }

        const isShareholderRange = typeof item.name === 'string'
          && (
            item.name.toLowerCase().includes('shareholders with greater than')
            || item.name.toLowerCase().includes('shareholders with less than')
          );

        // keep recipients that are not a shareholder range
        return !isShareholderRange;
      });

    return Object.assign(state, {
      form: Object.assign(state.form, {
        recipientGroups: newRecipientList,
      }),
    });
  },
  removeManualRecipients: (state) => {
    // keep recipients that are not manual
    const newRecipientList = state.form.recipientGroups.filter((item) => item.source !== 'manual');

    return Object.assign(state, {
      form: Object.assign(state.form, {
        recipientGroups: newRecipientList,
      }),
    });
  },
  // template list mutations
  setTemplatesLoading: (state, isLoading) => Object.assign(state, {
    templates: Object.assign(state.templates, {
      isLoading,
    }),
  }),
  setTemplatesData: (state, data) => Object.assign(state, {
    templates: Object.assign(state.templates, {
      data,
    }),
  }),
  // single template mutations
  setSingleTemplateLoading: (state, isLoading) => Object.assign(state, {
    singleTemplate: Object.assign(state.singleTemplate, {
      isLoading,
    }),
  }),
  setSingleTemplateData: (state, data) => Object.assign(state, {
    singleTemplate: Object.assign(state.singleTemplate, {
      data,
    }),
  }),
  // company settings mutations
  setCompanySettingsLoading: (state, isLoading) => Object.assign(state, {
    companySettings: Object.assign(state.companySettings, {
      isLoading,
    }),
  }),
  setCompanySettingsData: (state, data) => Object.assign(state, {
    companySettings: Object.assign(state.companySettings, {
      data,
    }),
  }),
};

const actions = {
  async updateCampaign(context, { id, payload, enableSaving = true }) {
    try {
      if (enableSaving) context.commit('setSaving', true);

      await emailCampaignsApi('patch', `/campaigns/${id}`, {
        params: {
          company_id: context.rootGetters['company/company_id'],
        },
        data: payload,
      });
      // re-populate campaign data
      await context.dispatch('getCampaign', { id, enableLoading: false });
    } finally {
      if (enableSaving) context.commit('setSaving', false);
    }
  },
  async createCampaign(context, { payload, enableSaving = true }) {
    try {
      if (enableSaving) context.commit('setSaving', true);

      const res = await emailCampaignsApi('post', '/campaigns', {
        params: {
          company_id: context.rootGetters['company/company_id'],
        },
        data: payload,
      });

      const campaignId = res.data.campaign_uid;

      await context.dispatch('getCampaign', {
        id: campaignId,
        isLoading: false,
      });

      return campaignId;
    } finally {
      if (enableSaving) context.commit('setSaving', false);
    }
  },
  async forwardCampaign(context, payload) {
    try {
      context.commit('setSaving', true);

      const data = context.state.form.data;

      let newTemplateId = null;
      // if original campaign has a template, make a copy to assign to new campaign
      if (data.template && data.template.uid) {
        const getTemplateRes = await emailCampaignsApi('get', `/templates/${data.template.uid}`, {
          params: {
            company_id: context.rootGetters['company/company_id'],
          },
        });
        const templateData = getTemplateRes.data.data;
        const postTemplateRes = await emailCampaignsApi('post', '/templates', {
          params: {
            company_id: context.rootGetters['company/company_id'],
          },
          data: {
            name: `${templateData.name} forward`,
            parent_template_uid: templateData.parent_template && templateData.parent_template.uid
              ? templateData.parent_template.uid
              : null,
            json: templateData.json,
          },
        });
        newTemplateId = postTemplateRes.data.template_uid;
      }

      const fullPayload = {
        // step setup
        name: data.name,
        run_at: data.run_at,
        subject: data.subject,
        // step recipients type
        type: data.type,
        // step recipients manual
        recipient_groups: [],
        // step recipients auto
        trigger_condition: data.trigger_condition,
        trigger_condition_threshold: data.trigger_condition_threshold,
        // step template
        template_uid: newTemplateId,
        // step template_editor
        attachments: data.attachments
          ? data.attachments.map((item) => item.uid)
          : [],
        // add fields from payload
        ...payload,
      };

      // create campaign and return campaign id
      return await context.dispatch('createCampaign', {
        enableSaving: false,
        payload: fullPayload,
      });
    } finally {
      context.commit('setSaving', false);
    }
  },
  async createCampaignWithRecipient(context, { campaignPayload, recipientPayload }) {
    try {
      context.commit('setSaving', true);

      const postRes = await emailCampaignsApi('post', '/recipient-groups/uuid', {
        params: {
          company_id: context.rootGetters['company/company_id'],
        },
        data: recipientPayload,
      });

      const recipientGroupId = postRes.data.recipient_group_uid;

      const fullCampaignPayload = {
        ...campaignPayload,
        recipient_groups: [
          recipientGroupId,
        ],
      };

      // create campaign and return campaign id
      return await context.dispatch('createCampaign', {
        enableSaving: false,
        payload: fullCampaignPayload,
      });
    } finally {
      context.commit('setSaving', false);
    }
  },
  async getCampaign(context, { id, enableLoading = true }) {
    try {
      if (enableLoading) context.commit('setLoading', true);

      const res = await emailCampaignsApi('get', `/campaigns/${id}`, {
        params: {
          company_id: context.rootGetters['company/company_id'],
        },
      });
      const data = res.data.data;

      // select recipients type if available
      if (data.type) {
        context.commit('selectRecipientsType', data.type);
      } else {
        context.commit('selectRecipientsType', null);
      }
      // set recipient groups
      if (data.recipient_groups) {
        context.commit('setRecipientGroups', data.recipient_groups);
      } else {
        context.commit('setRecipientGroups', []);
      }
      // select template if available
      if (data.template && data.template.uid) {
        context.commit('selectTemplate', data.template.uid);
      } else {
        context.commit('selectTemplate', null);
      }

      context.commit('setCampaignData', data);
    } catch (error) {
      if (campaignDoesNotBelongToCompany(error)) {
        throw new Error('UnauthorizedCompany');
      } else {
        throw error;
      }
    } finally {
      if (enableLoading) context.commit('setLoading', false);
    }
  },
  async getSingleTemplate(context, { id, enableLoading = true }) {
    try {
      if (enableLoading) context.commit('setSingleTemplateLoading', true);

      const res = await emailCampaignsApi('get', `/templates/${id}`, {
        params: {
          company_id: context.rootGetters['company/company_id'],
        },
      });
      const data = res.data.data;

      context.commit('setSingleTemplateData', data);
    } finally {
      if (enableLoading) context.commit('setSingleTemplateLoading', false);
    }
  },
  async getTemplates(context) {
    try {
      context.commit('setTemplatesLoading', true);

      const res = await emailCampaignsApi('get', '/templates', {
        params: {
          company_id: context.rootGetters['company/company_id'],
          format: 'json',
        },
      });
      const data = res.data.data;

      context.commit('setTemplatesData', data);
    } finally {
      context.commit('setTemplatesLoading', false);
    }
  },
  async saveTemplate(context, payload) {
    try {
      context.commit('setSaving', true);
      const selectedTemplateId = context.state.form.templateId;
      const campaignTemplateId = context.state.form.data.template
        ? context.state.form.data.template.uid
        : null;

      // if selected template is already assigned to campaign
      //   patch template
      // else
      //   post template and assign template to campaign
      if (selectedTemplateId === campaignTemplateId) {
        await emailCampaignsApi('patch', `/templates/${campaignTemplateId}`, {
          params: {
            company_id: context.rootGetters['company/company_id'],
          },
          data: payload,
        });
        // refresh template data
        await context.dispatch('getSingleTemplate', { id: campaignTemplateId });
      } else {
        const res = await emailCampaignsApi('post', '/templates', {
          params: {
            company_id: context.rootGetters['company/company_id'],
          },
          data: {
            name: `Default Name ${new Date()}`,
            ...payload,
            parent_template_uid: selectedTemplateId,
          },
        });

        const newTemplateId = res.data.template_uid;
        const campaignId = context.state.form.data.uid;

        // if no name provided in payload, set default template name based on template id
        if (!payload.name) {
          await emailCampaignsApi('patch', `/templates/${newTemplateId}`, {
            params: {
              company_id: context.rootGetters['company/company_id'],
            },
            data: {
              name: `Template ${newTemplateId}`,
            },
          });
        }

        await context.dispatch('updateCampaign', {
          id: campaignId,
          payload: {
            template_uid: newTemplateId,
          },
          enableSaving: false,
        });
        // refresh template data
        await context.dispatch('getSingleTemplate', { id: newTemplateId });
      }
    } finally {
      context.commit('setSaving', false);
    }
  },
  async testTemplate(context) {
    try {
      context.commit('setSaving', true);

      const selectedTemplateId = context.state.form.templateId;

      const res = await emailCampaignsApi('get', `/templates/${selectedTemplateId}/spam-warnings`, {
        params: {
          company_id: context.rootGetters['company/company_id'],
        },
      });

      return res.data.data;
    } finally {
      context.commit('setSaving', false);
    }
  },
  async getCompanySettings(context) {
    const { commit, rootGetters } = context;
    const companyId = rootGetters['company/company_id'];

    commit('setCompanySettingsLoading', true);

    try {
      const response = await emailCampaignsApi('get', '/settings', {
        params: { company_id: companyId },
      });

      const settings = {
        ...response.data.data,
        company_id: companyId, // For debugging purposes
      };

      commit('setCompanySettingsData', settings);
    } catch (error) {
      console.error('Failed to fetch company settings:', error);
      // Optional: commit an error state or use a notification
    } finally {
      commit('setCompanySettingsLoading', false);
    }
  },
  async updateCompanySettings(context, payload) {
    await emailCampaignsApi('patch', '/settings', {
      params: {
        company_id: context.rootGetters['company/company_id'],
      },
      data: payload,
    });
    // refresh company data
    await context.dispatch('getCompanySettings');
  },
  async uploadContactCsv(context, payload) {
    const companyId = context.rootGetters['company/company_id'];

    const postRes = await emailCampaignsApi('post', '/manual-contacts/csv', {
      params: {
        company_id: companyId,
        campaign_type: 'EMAIL',
      },
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      data: payload,
    }, 'data');

    const recipientGroupList = postRes.data;

    const recipientGroupPromises = recipientGroupList.map((item) => {
      const recipientGroupId = item.recipient_group_uid;
      return emailCampaignsApi('get', `/recipient-groups/${recipientGroupId}`, {
        params: { company_id: companyId },
      });
    });

    try {
      const responses = await Promise.all(recipientGroupPromises);
      responses.forEach((response) => {
        context.commit('appendRecipient', response.data.data);
      });
    } catch (error) {
      console.error('Error fetching recipient group details:', error);
      // Optionally commit an error state or show a notification
    }
  },
  async addManualContact(context, payload) {
    const { commit, rootGetters } = context;
    const companyId = rootGetters['company/company_id'];

    try {
      // Add the manual contact
      const postRes = await emailCampaignsApi('post', '/manual-contacts/single', {
        params: { company_id: companyId },
        data: payload,
      }, 'data');

      const recipientGroupId = postRes.data.recipient_group_uid;

      // Fetch updated recipient group details
      const getRes = await emailCampaignsApi('get', `/recipient-groups/${recipientGroupId}`, {
        params: { company_id: companyId },
      });

      commit('appendRecipient', getRes.data.data);
    } catch (error) {
      console.error('Error adding manual contact:', error);
      // Optional: commit an error state or show user-friendly notification
    }
  },
  async addRecipientGroupByUuidAndType(context, payload) {
    const { commit, rootGetters } = context;
    const companyId = rootGetters['company/company_id'];

    try {
      // Create a new recipient group
      const postRes = await emailCampaignsApi('post', '/recipient-groups/single', {
        params: { company_id: companyId },
        data: payload,
      }, 'data');

      const recipientGroupId = postRes.data.uid;

      // Fetch the newly created recipient group details
      const getRes = await emailCampaignsApi('get', `/recipient-groups/${recipientGroupId}`, {
        params: { company_id: companyId },
      });

      commit('appendRecipient', getRes.data.data);
    } catch (error) {
      console.error('Error adding recipient group:', error);
      // Optional: commit error state or notify user
    }
  },
};

const getters = {
  newTemplateLayouts: (state) => state.templates.data.filter((item) => item.type === 'blank'),
  premadeLayouts: (state) => state.templates.data.filter((item) => item.type === 'premade'),
  myLayouts: (state) => state.templates.data.filter((item) => item.type === 'user_owned'),
  manualRecipients: (state) => state.form.recipientGroups.filter((item) => item.source === 'manual'),
  csvRecipients: (state) => state.form.recipientGroups.filter((item) => item.source === 'csv'),
  contactRecipients: (state) => state.form.recipientGroups.filter((item) => item.source === 'contacts'),
  shareholderRecipients: (state) => state.form.recipientGroups.filter((item) => item.source === 'shareholders'),
  totalRecipientCount: (state) => state.form.recipientGroups
    .reduce((acc, item) => acc + item.count, 0),
  totalEmailCount: (state) => state.form.recipientGroups.reduce((acc, item) => {
    const emailCount = item.source !== 'manual' && item.count_unique != null ? item.count_unique : item.count;
    return acc + emailCount;
  }, 0),
  campaignsFeatureIsSetup: (state) => state.companySettings
    && state.companySettings.data
    && state.companySettings.data.email_enabled,
  campaignsFeatureIsActive: (state) => state.companySettings
    && state.companySettings.data
    && state.companySettings.data.client_status
    && state.companySettings.data.client_status === 'active',
  hasSmartSearchEnabled: (state) => state.companySettings
    && state.companySettings.data
    && state.companySettings.data.smart_search_enabled,
  hasShareholderSendingEnabled: (state) => state.companySettings
    && state.companySettings.data
    && state.companySettings.data.shareholder_sending_enabled,
  // object map of shareholder recipients based on source_uuid_type property
  shareholdersSourceTypeMap: (state) => state.form.recipientGroups.reduce((acc, item) => {
    if (item.source_uuid_type === 'shareholder') {
      acc[item.source_uuid] = true;
    }
    return acc;
  }, {}),
  // object map of shareholder recipients based on source property
  shareholdersSourceMap: (state) => state.form.recipientGroups.reduce((acc, item) => {
    if (item.source === 'shareholders') {
      acc[item.uid] = true;
    }
    return acc;
  }, {}),
  // object map of contact recipients based on source_uuid_type property
  contactsSourceTypeMap: (state) => state.form.recipientGroups.reduce((acc, item) => {
    if (item.source_uuid_type === 'contact') {
      acc[item.source_uuid] = true;
    }
    return acc;
  }, {}),
  // object map of contact recipients based on source property
  contactsSourceMap: (state) => state.form.recipientGroups.reduce((acc, item) => {
    if (item.source === 'contacts') {
      acc[item.uid] = true;
    }

    return acc;
  }, {}),
};

export default {
  state: InitialState,
  mutations,
  actions,
  getters,
  namespaced: true,
};
